import { IconButton } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { FC, ReactElement } from "react";
import { useState } from "react";
import { Dropdown } from "../../../../components/dropdown/dropdown";
import { Form } from "../../../../components/form/form";
import { Toggle } from "../../../../components/toggle/toggle";
import { useFactory } from "../../../../helpers/reactHooks";
import type { PaymentPageForm, WeekdayWorkingHoursData } from "../../../../types/paymentPage";
import { generateTimeOptions } from "../../helpers";
import type { FormSectionType } from "../../obPaymentPageForm";
import { EditFormModalSection } from "../editFormSection/editFormModalSection";
import styles from "./openingHoursSection.module.scss";

const days = ["Mon", "Tue", "Weds", "Thurs", "Fri", "Sat", "Sun"];
export const daysLong = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const getDayInfo = (dayData: WeekdayWorkingHoursData | undefined): string => {
  if (
    !dayData?.open_time ||
    !dayData?.close_time ||
    dayData?.close_time === "Closed" ||
    dayData?.open_time === "Closed"
  ) {
    return "Closed";
  }
  return `${dayData?.open_time} - ${dayData?.close_time}`;
};

type OpeningHoursForm = Pick<PaymentPageForm, "workingHours" | "switchedOnOpeningHours">;

// Both the page form and the modal form are edited through renderFormContent. Form<T> is invariant in T,
// so describe only the part it uses; method syntax keeps the parameters bivariant.
/* eslint-disable @typescript-eslint/method-signature-style -- property syntax would make Form<PaymentPageForm> not fit */
type OpeningHoursFields = {
  getValue<K extends keyof OpeningHoursForm>(field: K): OpeningHoursForm[K];
  onChange<K extends keyof OpeningHoursForm>(field: K, value: OpeningHoursForm[K]): void;
};
/* eslint-enable @typescript-eslint/method-signature-style */

const toTime = (value: string | number | null | undefined): Nullable<string> =>
  typeof value === "number" ? String(value) : value;

export const OpeningHoursSection: FC<FormSectionType> = observer(({ form, store }) => {
  const { workingHours, switchedOnOpeningHours } = form.values;

  const modalForm = useFactory(() => new Form<OpeningHoursForm>({ workingHours, switchedOnOpeningHours }));
  const [isFilled, setFilled] = useState(!!workingHours?.length);
  const [editMode, setEditMode] = useState(false);

  const handleToggle = (value: boolean, formData: OpeningHoursFields): void => {
    formData.onChange("switchedOnOpeningHours", value);
    if (!value) {
      formData.onChange("workingHours", []);
    }
  };

  const options = generateTimeOptions();

  const renderViewContent = (): ReactElement => (
    <div className={styles.viewSection}>
      <div className={styles.openingHoursSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionViewTitle}>
            <span className={styles.sectionSubtitle}>Opening hours</span>
          </div>

          <IconButton
            title="Edit opening hours"
            icon="actionEdit"
            iconColor="themeGreen"
            size="s"
            onClick={() => {
              setEditMode(true);
            }}
          />
        </div>

        <div className={styles.sectionViewContent}>
          {daysLong.map((day, i) => {
            const key = `days-info-${i}`;
            const dayData = workingHours?.find((el) => el.weekday === i + 1);
            return (
              <div className={styles.rowContent} key={key}>
                <span className={styles.text}>{day}</span>

                <span className={styles.hours}>{getDayInfo(dayData)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderFormContent = (formData: OpeningHoursFields, isModal: boolean): ReactElement => (
    <div className={classNames(styles.section, styles.withSeparator)}>
      <div className={styles.openingHoursSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionSubtitle}>Opening hours</span>
            <span className={styles.sectionDescription}>
              Include your openings hours to keep your customers informed of your hours of business
            </span>
          </div>
          <Toggle
            label="Opening hours"
            value={formData.getValue("switchedOnOpeningHours")}
            onChange={(value) => {
              handleToggle(value, formData);
            }}
          />
        </div>

        {formData.getValue("switchedOnOpeningHours") && (
          <>
            {days.map((day, i) => {
              const openingHours = formData.getValue("workingHours") ?? [];
              const key = `open-time-${i}`;
              const dayData = openingHours.find((el) => el.weekday === i + 1);
              return (
                <div className={styles.rowContent} key={key}>
                  <span className={classNames(styles.text, isModal && styles.modalText)}>{day}</span>
                  <div className={styles.dropdownGroup}>
                    <Dropdown
                      name={`open-time-${day}`}
                      className={styles.field}
                      label={i === 0 ? "From" : undefined}
                      value={dayData?.open_time}
                      options={options}
                      onChange={(v) => {
                        const existing = openingHours.find((el) => el.weekday === i + 1);
                        if (existing) {
                          // Mutates the observable entry in place, as the former lodash set path did
                          existing.open_time = toTime(v);
                        } else {
                          formData.onChange("workingHours", [
                            ...openingHours,
                            { open_time: toTime(v), close_time: null, weekday: i + 1 },
                          ]);
                        }
                      }}
                    />

                    <Dropdown
                      name={`close-time-${day}`}
                      className={styles.field}
                      label={i === 0 ? "To" : undefined}
                      options={options}
                      value={dayData?.close_time}
                      onChange={(v) => {
                        const existing = openingHours.find((el) => el.weekday === i + 1);
                        if (existing) {
                          // Mutates the observable entry in place, as the former lodash set path did
                          existing.close_time = toTime(v);
                        } else {
                          formData.onChange("workingHours", [
                            ...openingHours,
                            { open_time: null, close_time: toTime(v), weekday: i + 1 },
                          ]);
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );

  const handleCancelEditModal = (): void => {
    setEditMode(false);
    modalForm.onChange("workingHours", form.getValue("workingHours"));
  };

  const handleSaveEditModal = (): void => {
    setEditMode(false);
    if (modalForm.getValue("workingHours")?.length) {
      setFilled(true);
    } else {
      form.onChange("switchedOnOpeningHours", false);
      setFilled(false);
    }
    form.onChange("workingHours", modalForm.getValue("workingHours"));
  };

  if (store.firstUse || !isFilled) {
    return renderFormContent(form, false);
  }

  return (
    <>
      {renderViewContent()}
      <EditFormModalSection open={editMode} onCancel={handleCancelEditModal} onSave={handleSaveEditModal}>
        <div>{renderFormContent(modalForm, true)}</div>
      </EditFormModalSection>
    </>
  );
});
